import type {
  FormattedMember,
  FormattedMembers,
  FormattedMembersResponse,
  MemberDeactivationRequest,
  MemberProfile,
  MemberResponse,
  MemberTinStatusRequest,
  MembersFeatureFlag,
  MembersRequestWithFeatureFlags,
  MembersResponse,
  UpdateMemberRequest
} from '@library/types/members';
import type {
  FormattedMemberChangeLogs,
  MemberChangeLogsResponse
} from '@library/types/members/memberChangeLogs';
import type {
  GroupedMemberProfileMapCoordsList,
  MemberCoordsRequest,
  MemberSignInEntryCoordsResponse
} from '@library/types/memberProfileMap';
import { concat, mergeDeepWith } from 'ramda';
import {
  transformMemberResponse,
  transformMembers
} from '@bo-utils/members/members';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { defaultHeaders } from '@bo-constants/apiHeaders';
import { transformMemberChangeLogs } from '@bo-utils/members/memberChangeLogs';
import { transformMemberSignInCoords } from '@bo-utils/members/memberProfileMap';

const { member } = apiPaths;

export const userApi = api.injectEndpoints({
  endpoints: (build) => ({
    getMember: build.query<
      MemberProfile | undefined,
      { id: string; featureFlags: MembersFeatureFlag }
    >({
      query: ({ id }) => ({
        url: `${member}/${id}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: MemberResponse, _, { featureFlags }) =>
        transformMemberResponse({ memberResponse: response, featureFlags }),
      providesTags: ['Member']
    }),
    getMembers: build.query<
      FormattedMembersResponse | undefined,
      MembersRequestWithFeatureFlags
    >({
      query: ({
        featureFlags,
        ...membersRequest
      }: MembersRequestWithFeatureFlags) => ({
        url: member,
        method: 'GET',
        params: {
          ...membersRequest
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return `${endpointName}${Object.entries(rest).flat(1).join('')}`;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        return mergeDeepWith(
          (a: FormattedMember, b: FormattedMember) => {
            if (Array.isArray(a) && Array.isArray(b)) {
              return concat(a, b) as FormattedMembers;
            }
            return b;
          },
          currentCache,
          newItems
        ) as FormattedMembersResponse;
      },
      forceRefetch: ({ currentArg, previousArg }) =>
        JSON.stringify(currentArg) !== JSON.stringify(previousArg),
      transformResponse: (
        response: MembersResponse,
        _,
        { featureFlags, page, per_page }
      ) =>
        transformMembers({ response, page, perPage: per_page, featureFlags }),
      providesTags: ['Members']
    }),
    getMemberSignInCoords: build.query<
      GroupedMemberProfileMapCoordsList | undefined,
      MemberCoordsRequest
    >({
      query: ({ created_at_after, created_at_before, id }) => ({
        url: `${member}/${id}/sign_in_coords`,
        method: 'GET',
        params: {
          created_at_after,
          created_at_before
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: MemberSignInEntryCoordsResponse) =>
        transformMemberSignInCoords({ memberSignInCoordsResponse: response }),
      providesTags: ['MemberSignInCoords']
    }),
    getMemberEntriesCoords: build.query<
      GroupedMemberProfileMapCoordsList | undefined,
      MemberCoordsRequest
    >({
      query: ({ created_at_after, created_at_before, id }) => ({
        url: `${member}/${id}/entry_coords`,
        method: 'GET',
        params: {
          created_at_after,
          created_at_before
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      transformResponse: (response: MemberSignInEntryCoordsResponse) =>
        transformMemberSignInCoords({ memberSignInCoordsResponse: response }),
      providesTags: ['MemberEntryCoords']
    }),
    resetMemberPassword: build.mutation<void, { email: string }>({
      query: ({ email }) => ({
        url: 'users/password_reset',
        method: 'POST',
        body: {
          user: { email }
        },
        headers: defaultHeaders
      })
    }),
    enrollMemberInKindbridge: build.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `${member}/${id}/enroll_in_kindbridge`,
        method: 'POST',
        headers: defaultHeaders
      })
    }),
    getMemberChangeLogs: build.query<
      FormattedMemberChangeLogs | undefined,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${member}/${id}/user_notes`,
        method: 'GET',
        headers: defaultHeaders
      }),
      keepUnusedDataFor: 0,
      transformResponse: (response: MemberChangeLogsResponse) =>
        transformMemberChangeLogs(response),
      providesTags: ['MemberChangeLogs']
    }),
    updateMember: build.mutation<
      MemberProfile | undefined,
      UpdateMemberRequest
    >({
      query: ({ member_id, ...payload }) => ({
        url: `${member}/${member_id}`,
        method: 'PATCH',
        body: payload,
        headers: defaultHeaders
      }),
      invalidatesTags: ['Member']
    }),
    updateMemberTinStatus: build.mutation<
      MemberProfile | undefined,
      MemberTinStatusRequest
    >({
      query: ({ member_id, ...payload }) => ({
        url: `${member}/${member_id}/tin_status`,
        method: 'PATCH',
        body: payload,
        headers: defaultHeaders
      }),
      transformErrorResponse: ({ data }) => data,
      invalidatesTags: ['Member']
    }),
    manuallyPassMember: build.mutation<void, { member_id: string }>({
      query: ({ member_id }) => ({
        url: `${member}/${member_id}/manually_pass_user`,
        method: 'POST',
        headers: defaultHeaders
      }),
      invalidatesTags: ['Member']
    }),
    deactivateMember: build.mutation<void, MemberDeactivationRequest>({
      query: ({ member_id, ...payload }) => ({
        url: `${member}/${member_id}/deactivate`,
        method: 'PATCH',
        body: payload,
        headers: defaultHeaders
      }),
      invalidatesTags: ['Member']
    }),
    reactivateMember: build.mutation<void, { member_id: string }>({
      query: ({ member_id }) => ({
        url: `${member}/${member_id}/reactivate`,
        method: 'POST',
        headers: defaultHeaders
      }),
      invalidatesTags: ['Member']
    })
  })
});

export const {
  useDeactivateMemberMutation,
  useEnrollMemberInKindbridgeMutation,
  useGetMemberChangeLogsQuery,
  useGetMemberEntriesCoordsQuery,
  useGetMemberSignInCoordsQuery,
  useGetMemberQuery,
  useGetMembersQuery,
  useManuallyPassMemberMutation,
  useReactivateMemberMutation,
  useResetMemberPasswordMutation,
  useUpdateMemberMutation,
  useUpdateMemberTinStatusMutation
} = userApi;
